import { useEffect, useState } from 'react';
import { brand } from '../lib/brand';

type Props = {
    /**
     * Optional human-readable status surfaced under the spinner. Useful for
     * "идёт обновление…" / "соединение…" cases where the splash may be on
     * screen for more than the usual ~700 ms.
     */
    status?: string;
    /**
     * If we've been on screen this long without unmounting, show the skip
     * banner with a hint that something might be slow. Default 5s.
     */
    slowAfterMs?: number;
};

/**
 * Splash shown while the bridge boots and first state loads.
 *
 * Improvements over the previous version:
 *  * Falls back to a CSS-only spinner if the GIF asset 404s — no broken-
 *    image icon in the WebView, and the splash still looks intentional in
 *    a fresh clone where /loading/loading.gif hasn't been added yet.
 *  * Surfaces a "это занимает дольше обычного" hint after a configurable
 *    delay so users on slow networks know they aren't frozen.
 *  * Uses `prefers-reduced-motion` to skip animation if the OS asks.
 *  * Locks the GIF to a max display time per object-fit to avoid pixel
 *    bleed on tall phones.
 */
export function LoadingScreen({ status, slowAfterMs = 5000 }: Props) {
    const [imgFailed, setImgFailed] = useState(false);
    const [slow, setSlow] = useState(false);

    useEffect(() => {
        const id = window.setTimeout(() => setSlow(true), slowAfterMs);
        return () => window.clearTimeout(id);
    }, [slowAfterMs]);

    return (
        <div className="loading-screen" role="status" aria-live="polite" aria-label="Загрузка">
            <div className="loading-stage">
                {!imgFailed && brand.splashImage && (
                    <img
                        className="loading-image"
                        src={brand.splashImage}
                        alt=""
                        draggable={false}
                        onError={() => setImgFailed(true)}
                    />
                )}
                {(imgFailed || !brand.splashImage) && (
                    <div className="loading-fallback" aria-hidden>
                        <div className="loading-spinner" />
                        <div className="loading-brand">BogStars</div>
                    </div>
                )}
            </div>

            <div className="loading-meta">
                <div className="loading-status">{status ?? 'Загружаем…'}</div>
                {slow && (
                    <div className="loading-hint">
                        Соединение медленное. Если что — закройте и откройте Mini App ещё раз.
                    </div>
                )}
            </div>
        </div>
    );
}
